import { useCallback, useState } from "react";

import useAutomationJobStatus, { AutomationJobStatus } from "./useAutomationJobStatus";
import useDownloadAutomationResult from "./useDownloadAutomationResult";
import useExecuteAutomation from "./useExecuteAutomation";

export interface AutomationExecutionState {
  isStartingJob: boolean;
  isCheckingStatus: boolean;
  isDownloading: boolean;
  isProcessing: boolean;
  currentJobId: string | null;
  jobStatus: AutomationJobStatus | null;
  progress: number;
  statusText: string;
}

export interface AutomationExecutionActions {
  executeAutomation: (automationId: string, automationName?: string) => Promise<void>;
  cancelJob: () => void;
  downloadResult: () => Promise<void>;
}

export default function useAutomationExecution(): AutomationExecutionState &
  AutomationExecutionActions {
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentAutomationName, setCurrentAutomationName] = useState<string | null>(null);

  const { mutateAsync: startJob, isLoading: isStartingJob } = useExecuteAutomation();
  const { mutateAsync: downloadResult, isLoading: isDownloading } =
    useDownloadAutomationResult();

  const { data: jobStatus, isLoading: isCheckingStatus } = useAutomationJobStatus(
    currentJobId,
    {
      onSuccess: (data) => {
        if (data.status === "completed") {
          // Auto-download when completed
          if (currentJobId) {
            downloadResult({
              jobId: currentJobId,
              automationName: currentAutomationName || undefined
            })
              .then(() => {
                setCurrentJobId(null); // Clear job after successful download
                setCurrentAutomationName(null);
              })
              .catch(() => {
                // Error will be handled by UI
              });
          }
        } else if (data.status === "failed") {
          setCurrentJobId(null); // Clear failed job
          setCurrentAutomationName(null);
        }
      },
      onError: () => {
        setCurrentJobId(null); // Clear job on status error
        setCurrentAutomationName(null);
      }
    }
  );

  const executeAutomation = useCallback(
    async (automationId: string, automationName?: string) => {
      if (!automationId) {
        throw new Error("No automation ID provided");
      }

      if (currentJobId) {
        throw new Error(
          "An automation job is already running. Please wait for it to complete."
        );
      }

      const response = await startJob(automationId);
      setCurrentJobId(response.jobId);
      setCurrentAutomationName(automationName || null);
    },
    [startJob, currentJobId]
  );

  const cancelJob = useCallback(() => {
    if (currentJobId) {
      setCurrentJobId(null);
      setCurrentAutomationName(null);
    }
  }, [currentJobId]);

  const manualDownload = useCallback(async () => {
    if (!currentJobId) {
      throw new Error("No job ID available for download");
    }

    await downloadResult({
      jobId: currentJobId,
      automationName: currentAutomationName || undefined
    });
    setCurrentJobId(null);
    setCurrentAutomationName(null);
  }, [currentJobId, downloadResult]);

  const getStatusText = useCallback(() => {
    if (!jobStatus) return "";

    switch (jobStatus.status) {
      case "waiting":
        return "Job queued, waiting to start...";
      case "active":
        return "Job running...";
      case "completed":
        return "Job completed, downloading...";
      case "failed":
        return "Job failed";
      case "delayed":
        return "Job delayed, will retry...";
      default:
        return `Status: ${jobStatus.status}`;
    }
  }, [jobStatus]);

  const isProcessing = isStartingJob || isCheckingStatus || isDownloading;

  return {
    // State
    isStartingJob,
    isCheckingStatus,
    isDownloading,
    isProcessing,
    currentJobId,
    jobStatus,
    progress: jobStatus?.progress || 0,
    statusText: getStatusText(),

    // Actions
    executeAutomation,
    cancelJob,
    downloadResult: manualDownload
  };
}
